import React, { useMemo } from "react";
import { Text, Stack } from "@mantine/core";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import {
  formatDate,
  formatDaysOfWeek,
  formatUTCTimeRange,
  RecurringEvent,
} from "@/utils";
import { EventRoomsInfoBadge } from "../EventRoomsInfoBadge";
import { EventTeamsInfoBadge } from "../EventTeamsInfoBadge";
import { TagsBadge } from "../TagsBadge";

export interface RecurringEventsTableProps {
  /** Array of recurring events to display */
  recurringEvents: RecurringEvent[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditRecurringEvent?: (event: RecurringEvent) => void;

  /** Callback when remove action is clicked */
  onRemoveRecurringEvent?: (event: RecurringEvent) => void;

  /** Callback when view action is clicked */
  onViewRecurringEvent?: (event: RecurringEvent) => void;

  /** ID of recurring event currently being removed */
  removingRecurringEventId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const RecurringEventsTable: React.FC<RecurringEventsTableProps> = ({
  recurringEvents,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditRecurringEvent,
  onRemoveRecurringEvent,
  onViewRecurringEvent,
  removingRecurringEventId,
  size = "md",
}) => {
  // Helper function to format date range
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate && !endDate) return "-";
    if (!endDate) return startDate || "-";
    if (!startDate) return endDate || "-";

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    return (
      <Stack gap={0}>
        <Text>{start}</Text>
        <Text>{end}</Text>
      </Stack>
    );
  };

  // Define table columns
  const columns: AppTableColumn<RecurringEvent>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Event Name",
        sortable: true,
        flex: true,
        minWidth: 180,
        render: (value: any, record: RecurringEvent) => (
          <Stack gap={0}>
            <Text fw={600}>{value || "Untitled Event"}</Text>
            <EventTeamsInfoBadge event={record} />
            <TagsBadge
              value={record.categories?.map((category) => category?.name ?? "")}
            />
          </Stack>
        ),
      },
      {
        key: "recurrencePattern",
        label: "Pattern",
        sortable: true,
        width: 180,
        minWidth: 180,
        render: (value, record) => (
          <Stack gap={0}>
            <Text fw={600} style={{ textTransform: "capitalize" }}>
              {value || "Custom"}:
            </Text>
            <Text>
              {formatDaysOfWeek(record.daysOfWeek, record.recurrencePattern)}
            </Text>
          </Stack>
        ),
      },
      {
        key: "startDate",
        label: "Date Range",
        sortable: true,
        width: 160,
        minWidth: 160,
        render: (value, record) => formatDateRange(value, record.endDate),
      },
      {
        key: "startTime",
        label: "Time",
        width: 180,
        minWidth: 180,
        render: (value, record) => (
          <Text>
            {formatUTCTimeRange(value, record.endTime, "h:mm A", true)}
          </Text>
        ),
      },
      {
        key: "room",
        label: "Room",
        width: 250,
        minWidth: 250,
        render: (_, record) => <EventRoomsInfoBadge event={record} />,
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 120,
        minWidth: 120,
        align: "left",
        render: (_, record) => (
          <RecordActions
            context={record}
            //onViewClick={onViewRecurringEvent}
            onEditClick={onEditRecurringEvent}
            onRemoveClick={onRemoveRecurringEvent}
            removeInProgress={removingRecurringEventId === record.id}
          />
        ),
      },
    ],
    [
      onViewRecurringEvent,
      onEditRecurringEvent,
      onRemoveRecurringEvent,
      removingRecurringEventId,
    ]
  );

  return (
    <AppTable
      data={recurringEvents}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(event) => event?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
